/**
 * Part 11g: the modal, every dialog's one look (PLAN.md §3, step 3: the library Dialog's parts, in the app's one native
 * `<dialog>`; before it, cockatrice-ui `components/books/Sheet.jsx`): a head
 * with the dialog's name, an optional line above or below it and Close; a body that scrolls; a foot with a note on the
 * left and the actions on the right. On a phone it is the whole screen, its head and foot fixed (table MD, part 11g log).
 *
 * The app has one native modal `<dialog>` (`dialogs.ts`); these are the parts each dialog's content is made of. It
 * closes four ways, all through `ui.setDialog(null)`, so the dialog's close handler runs the same for each: Close in the
 * head, Escape, a click or tap on the backdrop ({@link closesOnBackdrop}), and the dialog's own actions.
 */
import type { Accessor } from 'solid-js';
import { DialogActions, DialogBody, DialogFoot, DialogHead, DialogNote } from '../ui/Dialog.ts';
import { component } from '../ui/solid.ts';
import type { Attributes, Child, On } from '../ui/dom.ts';
import type { Shell } from './shell.ts';

/** What a head shows besides the name: a line above it (an account sheet's step), a line under it, and Close's own class. */
export interface HeadOptions {
  readonly progress?: Accessor<string> | string | null;
  readonly subtitle?: Child;
  /** A class Close carries besides the modal's (what a test or a focus move finds it by). */
  readonly closeClass?: string;
  /** Whether the name takes the dialog's first focus (a long dialog opens at its start, M7), not its first control. */
  readonly focus?: boolean;
}

/**
 * The head: the name (`#dialog-heading`, which names the dialog and takes the first focus where a dialog asks for it)
 * and Close (PLAN.md §3, step 3: the library's DialogHead).
 */
export function ModalHead(shell: Shell, title: Child, options: HeadOptions = {}): Child {
  const { t, ui } = shell;
  return component(DialogHead, {
    title,
    titleId: 'dialog-heading',
    titleFocusable: true,
    titleAutofocus: options.focus === true,
    eyebrow: options.progress ?? undefined,
    description: options.subtitle,
    closeLabel: t('modal.close'),
    closeClass: options.closeClass === undefined ? 'modal-close' : `modal-close ${options.closeClass}`,
    onClose: () => {
      ui.setDialog(null);
    },
  });
}

/** The body, which scrolls between the head and the foot (`modal-body` is the hook the screens' layout rules use). */
export function ModalBody(attributes: Attributes, ...children: readonly Child[]): Child {
  const extra = attributes['class'];
  return component(DialogBody, { ...attributes, class: typeof extra === 'string' ? `modal-body ${extra}` : 'modal-body', children });
}

/** The foot: a note (if any) on the left, the actions on the right; on a phone, the actions full width and stacked. */
export function ModalFoot(attributes: Attributes, ...children: readonly Child[]): Child {
  const extra = attributes['class'];
  return component(DialogFoot, { ...attributes, class: typeof extra === 'string' ? `modal-foot ${extra}` : 'modal-foot', children });
}

/** The foot's actions, kept together at its right (the primary last, as the design has it). */
export function ModalActions(...children: readonly Child[]): Child {
  return component(DialogActions, { children });
}

/** A foot that holds only buttons: they go in its actions, at its right (on a phone, full width, the primary on top). */
export function ModalButtons(...children: readonly Child[]): Child {
  return ModalFoot({}, ModalActions(...children));
}

/** The foot's note, at its left: a small line of help. */
export function ModalNote(...children: readonly Child[]): Child {
  return component(DialogNote, { children });
}

/** Whether a pointer event on a dialog was on its backdrop: the dialog receives a press beside its box as its own. */
function onBackdrop(event: On<PointerEvent, HTMLDialogElement>): boolean {
  if (event.target !== event.currentTarget) return false;
  const box = event.currentTarget.getBoundingClientRect();
  return event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom;
}

/**
 * MD3: the dialog's handlers that close it on a click or tap on the backdrop: pressed and released there, so a drag
 * that starts in a field (selecting its text) and ends beside the box closes nothing and loses nothing typed. The
 * release, not the click: Safari sends a tap on a plain element as no click at all (part 11c, the drawer's r5).
 */
export function closesOnBackdrop(close: () => void): Attributes {
  let pressed = false;
  return {
    onPointerDown: (event: On<PointerEvent, HTMLDialogElement>) => {
      pressed = onBackdrop(event);
    },
    onPointerUp: (event: On<PointerEvent, HTMLDialogElement>) => {
      const both = pressed && onBackdrop(event);
      pressed = false;
      if (both) close();
    },
  };
}
